import { defineStore } from 'pinia';
import {
  fetchAllBillruns,
  fetchAnalysisForBillrun,
} from '@/services/billrunsDataFetchingService';
import { interpretBillrunEnergyDataAnalysis } from '@/services/billrunEnergyDataInterpretationService';
import { filterBillingMasterData } from '@/services/billrunFiltersService';

const ANALYSIS_FETCH_CONCURRENCY = 5;

export function billRunAnalysisKey(ecid, runIdentifier) {
  return `${ecid}:${runIdentifier}`;
}

function buildInterpretationMap(billruns, analysisByBillRunKey, billingRelevantOnly) {
  const options = { billingRelevantOnly };
  const map = {};
  for (const br of billruns) {
    const key = billRunAnalysisKey(br.ecid, br.runIdentifier);
    map[key] = interpretBillrunEnergyDataAnalysis(analysisByBillRunKey[key] ?? null, options);
  }
  return map;
}

async function runWithConcurrency(items, concurrency, fn) {
  if (items.length === 0) {
    return [];
  }
  const results = new Array(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const index = nextIndex++;
      results[index] = await fn(items[index], index);
    }
  }

  const workerCount = Math.min(concurrency, items.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}

export const usebillrunsStore = defineStore('billRuns', {
  state: () => ({
    offsetDays: 15,
    billingRelevantMeterCodesOnly: true,
    billruns: [],
    hasFetched: false,
    loading: false,
    error: null,
    analysisByBillRunKey: {},
    interpretationByBillRunKey: {},
    analysisLoading: false,
    analysisError: null,
  }),

  actions: {
    async fetchAllBillingMasterData() {
      this.loading = true;
      this.error = null;
      this.analysisError = null;
      this.analysisByBillRunKey = {};
      this.interpretationByBillRunKey = {};
      try {
        const allBillruns = await fetchAllBillruns();
        const list = Array.isArray(allBillruns) ? allBillruns : [];
        const offset = Number(this.offsetDays);
        const safeOffset = Number.isFinite(offset) && offset >= 0 ? offset : 15;
        this.billruns = filterBillingMasterData(list, safeOffset);
        this.hasFetched = true;

        if (this.billruns.length > 0) {
          await this.fetchAnalysisForBillRuns();
        }
      } catch (error) {
        this.error = error?.message || String(error);
        this.billruns = [];
        this.analysisByBillRunKey = {};
        this.interpretationByBillRunKey = {};
        this.hasFetched = true;
        throw error;
      } finally {
        this.loading = false;
        this.analysisLoading = false;
      }
    },

    async fetchAnalysisForBillRuns() {
      const billrunsWithIdentifier = this.billruns.filter((br) => br.runIdentifier);
      if (billrunsWithIdentifier.length === 0) {
        this.analysisByBillRunKey = {};
        this.interpretationByBillRunKey = {};
        return;
      }

      this.analysisLoading = true;
      this.analysisError = null;

      const outcomes = await runWithConcurrency(
        billrunsWithIdentifier,
        ANALYSIS_FETCH_CONCURRENCY,
        async (br) => {
          try {
            const analysis = await fetchAnalysisForBillrun(br.runIdentifier);
            return {
              status: 'fulfilled',
              ecid: br.ecid,
              runIdentifier: br.runIdentifier,
              analysis,
            };
          } catch (error) {
            return {
              status: 'rejected',
              runIdentifier: br.runIdentifier,
              reason: error,
            };
          }
        }
      );

      const map = {};
      const errors = [];
      for (const outcome of outcomes) {
        if (outcome.status === 'fulfilled') {
          map[billRunAnalysisKey(outcome.ecid, outcome.runIdentifier)] = outcome.analysis;
        } else {
          errors.push(
            `${outcome.runIdentifier}: ${outcome.reason?.message || String(outcome.reason)}`
          );
        }
      }

      this.analysisByBillRunKey = map;
      this.interpretationByBillRunKey = buildInterpretationMap(
        this.billruns,
        map,
        this.billingRelevantMeterCodesOnly
      );

      if (errors.length > 0) {
        this.analysisError = errors.join('; ');
      }

      this.analysisLoading = false;
    },

    setBillingRelevantMeterCodesOnly(value) {
      this.billingRelevantMeterCodesOnly = Boolean(value);
      if (Object.keys(this.analysisByBillRunKey).length > 0) {
        this.interpretationByBillRunKey = buildInterpretationMap(
          this.billruns,
          this.analysisByBillRunKey,
          this.billingRelevantMeterCodesOnly
        );
      }
    },
  },
});
